import { memo, useCallback, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { styled } from "@mui/material";
import Tooltip, { tooltipClasses, type TooltipProps } from "@mui/material/Tooltip";
import useMergeProps from "hooks/useMergeProps";

const DEFAULT_PROPS = {
  disableFocusListener: true,
  disableInteractive: true,
  enterDelay: 400,
  slotProps: {
    transition: { timeout: { enter: 150, exit: 50 } },
  },
  mode: "dark",
  placement: "top",
} as const;

/**
 * Either a predicate (`() => boolean`) or a ref to a DOM element.
 *
 * - **Predicate** — full control; return `true` to allow the tooltip to open.
 * - **Ref** — convenience; the tooltip opens only when the referenced element
 *   is overflowing (clamped) horizontally or vertically. Useful for showing
 *   the full text only when a `<Typography noWrap>` or line-clamped element
 *   has been truncated.
 */
export type CanAppear = (() => boolean) | RefObject<HTMLElement | null>;

export interface TooltipHelperProps extends TooltipProps {
  mode?: "light" | "dark";
  /**
   * Controls whether the tooltip is allowed to open. When omitted, the tooltip behaves as a normal MUI Tooltip.
   *
   * @example Predicate
   *   <TooltipHelper title="Save" canAppear={() => isFeatureEnabled}>
   *     <IconButton>…</IconButton>
   *   </TooltipHelper>
   *
   * @example Ref — only show on overflow
   *   const ref = useRef<HTMLSpanElement>(null);
   *   <TooltipHelper title={text} canAppear={ref}>
   *     <Typography ref={ref} noWrap>{text}</Typography>
   *   </TooltipHelper>
   */
  canAppear?: CanAppear;
}

/**
 * Resolves a `CanAppear` value into a boolean decision.
 * - Undefined → always allow (matches a normal MUI Tooltip).
 * - Function → call it, use its return value.
 * - Ref → check whether the element is overflowing in either axis.
 */
function shouldAppear(value: CanAppear | undefined): boolean {
  if (value === undefined) return true;
  if (typeof value === "function") return value();
  const el = value.current;
  if (!el) return false;
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}

const TooltipHelper = styled((props: TooltipHelperProps) => {
  const { mode, canAppear, className, ...rest } = useMergeProps(DEFAULT_PROPS, props);

  const [open, setOpen] = useState(false);

  // Stable ref so handler identity isn't bound to `canAppear`'s identity
  const canAppearRef = useRef(canAppear);
  canAppearRef.current = canAppear;

  const handleOpen = useCallback(() => {
    if (shouldAppear(canAppearRef.current)) setOpen(true);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  // Only take control of MUI's open state when `canAppear` is present
  const gatingProps = useMemo(() => {
    if (canAppear === undefined) return {};
    return { open, onOpen: handleOpen, onClose: handleClose };
  }, [canAppear, open, handleOpen, handleClose]);

  return (
    <Tooltip
      {...rest}
      {...gatingProps}
      data-mode={mode}
      classes={{ popper: className }}
      slotProps={{
        ...rest.slotProps,
        tooltip: {
          ...rest.slotProps?.tooltip,
          // @ts-ignore
          "data-mode": mode,
        },
      }}
    />
  );
})(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.TextXS.fontSize,
    fontWeight: "500",
    lineHeight: "18px",
    borderRadius: "4px",

    "&[data-mode='dark']": {
      backgroundColor: theme.palette.grey[120],
      color: theme.palette.common.white,
    },
    "&[data-mode='light']": {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
    },
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
    marginTop: "10px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
    marginBottom: "10px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
    marginLeft: "10px",
  },
  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
    marginRight: "10px",
  },
}));

export default memo(TooltipHelper);
